import {PlusCircleIcon, PlusIcon} from '@primer/octicons-react'
import {ActionList} from '@primer/react'
import {useCallback, useMemo} from 'react'

import {groupDiffLines, isContextDiffLine, isEmptyDiffLine} from '../helpers/line-helpers'
import type {ClientDiffLine, DiffLine} from '../types'

import {KeybindingHint} from '@primer/react/experimental'
import {useDiffLinesActions, useGetSelectedDiffLines, useGetSelectedLineRange} from '../stores/selectors'
import {handleLineSelectionEffects} from '../helpers/line-selection'
import {diffLineKey} from '../stores/slices/helpers/diff-lines-slice-helpers'

const ORIENTATION_MAP = {
  left: 'L',
  right: 'R',
}

const START_CONVERSATION = 'Add comment'
const SUGGEST_CHANGE = 'Suggest change'

type ContextMenuActions = typeof START_CONVERSATION | typeof SUGGEST_CHANGE

function generateSingleLineText(baseAction: ContextMenuActions, diffLine: DiffLine, isLeftSide?: boolean) {
  const side = isLeftSide && !isContextDiffLine(diffLine) ? ORIENTATION_MAP.left : ORIENTATION_MAP.right
  return `${baseAction} on line ${side}${diffLine.blobLineNumber}`
}

function generateModifiedRangeText(
  firstLine: DiffLine,
  lastLine: DiffLine,
  originalLinesAreEmpty: boolean,
  isSplit: boolean | undefined,
  allLinesAreContextLines: boolean,
) {
  const hasOneLineOnModifiedSide = firstLine.right === lastLine.right
  if (hasOneLineOnModifiedSide) {
    return originalLinesAreEmpty || (isSplit && allLinesAreContextLines)
      ? ` on line R${firstLine.right}`
      : `, modified line R${lastLine.right}`
  } else {
    return originalLinesAreEmpty || (isSplit && allLinesAreContextLines)
      ? ` on lines R${firstLine.right}-R${lastLine.right}`
      : `, modified lines R${firstLine.right}-R${lastLine.right}`
  }
}

function generateOriginalRangeText(firstLine: DiffLine, lastLine: DiffLine, modifiedLinesAreEmpty: boolean) {
  const hasOneLineOnOriginalSide = firstLine.left === lastLine.left
  if (hasOneLineOnOriginalSide) {
    return modifiedLinesAreEmpty ? ` on line L${firstLine.left}` : `, original line L${firstLine.left}`
  } else {
    return modifiedLinesAreEmpty
      ? ` on lines L${firstLine.left}-L${lastLine.right}`
      : `, original lines L${firstLine.left}-L${lastLine.left}`
  }
}

/**
 * Renders context-specific context menu conversation selection options for diffline cell(s) based on split and unified views, the number of lines selected and the orientation of those selected lines.
 *
 * @param handleStartConversationWithSuggestedChange a function invoked when starting a conversation with suggested change
 * @param handleStartConversation a function invoked when starting a conversation
 */
export function StartConversationContextMenuItems({
  diffLine,
  handleStartConversationWithSuggestedChange,
  handleStartConversation,
  isLeftSide,
  isSplit,
}: {
  diffLine: ClientDiffLine
  handleStartConversationWithSuggestedChange: () => void
  handleStartConversation: () => void
  isLeftSide: boolean
  isSplit: boolean
}) {
  const line = diffLine as DiffLine
  const {selectMultipleLines} = useDiffLinesActions()
  const getSelectedDiffLines = useGetSelectedDiffLines()
  const getSelectedLineRange = useGetSelectedLineRange()
  const selectedLineRange = getSelectedLineRange()

  const selectedDiffLines = useMemo(() => {
    return groupDiffLines(getSelectedDiffLines())
  }, [getSelectedDiffLines])

  // Get the relevant lines for the selected diff row range
  const firstOriginalLine = useMemo(
    () => selectedDiffLines.leftLines.find(leftLine => !isEmptyDiffLine(leftLine)),
    [selectedDiffLines.leftLines],
  )
  const lastOriginalLine = useMemo(() => {
    const nonEmptyLines = selectedDiffLines.leftLines.filter(leftLine => !isEmptyDiffLine(leftLine))
    return nonEmptyLines[nonEmptyLines.length - 1]
  }, [selectedDiffLines.leftLines])
  const allOriginalLinesNotDeletions = useMemo(
    () =>
      (selectedDiffLines.leftLines.filter(leftLine => !isEmptyDiffLine(leftLine)) as DiffLine[]).every(
        leftLine => leftLine.type !== 'DELETION',
      ),
    [selectedDiffLines.leftLines],
  )
  const firstModifiedLine = useMemo(
    () => selectedDiffLines.rightLines.find(rightLine => !isEmptyDiffLine(rightLine)),
    [selectedDiffLines.rightLines],
  )
  const lastModifiedLine = useMemo(() => {
    const nonEmptyLines = selectedDiffLines.rightLines.filter(rightLine => !isEmptyDiffLine(rightLine))
    return nonEmptyLines[nonEmptyLines.length - 1]
  }, [selectedDiffLines.rightLines])

  const modifiedLinesAreEmpty = useMemo(
    () => selectedDiffLines.rightLines.filter(rightLine => !isEmptyDiffLine(rightLine)).length === 0,
    [selectedDiffLines.rightLines],
  )
  const originalLinesAreEmpty = useMemo(
    () => selectedDiffLines.leftLines.filter(leftLine => !isEmptyDiffLine(leftLine)).length === 0,
    [selectedDiffLines.leftLines],
  )
  const allLinesAreContextLines = useMemo(() => {
    if (
      selectedDiffLines.leftLines.find(
        leftLine => !isEmptyDiffLine(leftLine) && !isContextDiffLine(leftLine) && leftLine.type !== 'INJECTED_CONTEXT',
      )
    )
      return false
    if (
      selectedDiffLines.rightLines.find(
        rightLine =>
          !isEmptyDiffLine(rightLine) && !isContextDiffLine(rightLine) && rightLine.type !== 'INJECTED_CONTEXT',
      )
    )
      return false

    return true
  }, [selectedDiffLines.leftLines, selectedDiffLines.rightLines])

  // Handlers for starting conversations based on selected lines
  const handleStartOriginalLinesConversationSelection = useCallback(
    (e: React.KeyboardEvent | React.MouseEvent, opts?: {withSuggestedChange?: boolean}) => {
      if (
        !selectedLineRange ||
        !lastOriginalLine ||
        !firstOriginalLine ||
        isEmptyDiffLine(lastOriginalLine) ||
        isEmptyDiffLine(firstOriginalLine) ||
        !firstOriginalLine.left ||
        !lastOriginalLine.left
      ) {
        return
      }

      selectMultipleLines(
        {
          endLineKey: diffLineKey(lastOriginalLine),
          endOrientation: 'left',
          startLineKey: diffLineKey(firstOriginalLine),
          startOrientation: 'left',
          firstLineSelectionOnDrag: {
            lineKey: diffLineKey(firstOriginalLine),
            lineNumber: firstOriginalLine.left,
            orientation: 'left',
            shouldStartConversation: false,
          },
        },
        handleLineSelectionEffects,
      )
      if (opts?.withSuggestedChange) {
        handleStartConversationWithSuggestedChange()
      } else {
        handleStartConversation()
      }
    },
    [
      selectedLineRange,
      lastOriginalLine,
      firstOriginalLine,
      selectMultipleLines,
      handleStartConversationWithSuggestedChange,
      handleStartConversation,
    ],
  )

  const handleStartModifiedLinesConversationSelection = useCallback(
    (e: React.KeyboardEvent | React.MouseEvent, opts?: {withSuggestedChange?: boolean}) => {
      if (
        !selectedLineRange ||
        !lastModifiedLine ||
        !firstModifiedLine ||
        isEmptyDiffLine(lastModifiedLine) ||
        isEmptyDiffLine(firstModifiedLine) ||
        !firstModifiedLine.right ||
        !lastModifiedLine.right
      ) {
        return
      }

      selectMultipleLines(
        {
          endLineKey: diffLineKey(lastModifiedLine),
          endOrientation: 'right',
          startLineKey: diffLineKey(firstModifiedLine),
          startOrientation: 'right',
          firstLineSelectionOnDrag: {
            lineKey: diffLineKey(firstModifiedLine),
            lineNumber: firstModifiedLine.right,
            orientation: 'right',
            shouldStartConversation: false,
          },
        },
        handleLineSelectionEffects,
      )
      if (opts?.withSuggestedChange) {
        handleStartConversationWithSuggestedChange()
      } else {
        handleStartConversation()
      }
    },
    [
      selectedLineRange,
      lastModifiedLine,
      firstModifiedLine,
      selectMultipleLines,
      handleStartConversationWithSuggestedChange,
      handleStartConversation,
    ],
  )
  const canSuggestChanges = line.type !== 'DELETION'
  const hasMultipleLinesSelected: boolean = useMemo(() => {
    if (!selectedLineRange) return false
    if (selectedLineRange.startOrientation !== selectedLineRange.endOrientation) return true
    if (selectedLineRange.startLineNumber !== selectedLineRange.endLineNumber) return true
    return false
  }, [selectedLineRange])

  let originalRangeText
  let modifiedRangeText
  let unifiedRangeText
  let showOriginalLineOption: boolean = false
  let showModifiedLineOption: boolean = false

  // multi-line selection
  if (selectedLineRange && hasMultipleLinesSelected) {
    const selectedStartOrientation = ORIENTATION_MAP[selectedLineRange.startOrientation]
    const selectedEndOrientation = ORIENTATION_MAP[selectedLineRange.endOrientation]

    unifiedRangeText = ` on lines ${selectedStartOrientation}${selectedLineRange.startLineNumber}-${selectedEndOrientation}${selectedLineRange.endLineNumber}`

    if (
      !isEmptyDiffLine(firstOriginalLine) &&
      !isEmptyDiffLine(lastOriginalLine) &&
      firstOriginalLine &&
      lastOriginalLine
    ) {
      showOriginalLineOption = true
      originalRangeText = generateOriginalRangeText(firstOriginalLine, lastOriginalLine, modifiedLinesAreEmpty)
    }

    if (
      !isEmptyDiffLine(firstModifiedLine) &&
      !isEmptyDiffLine(lastModifiedLine) &&
      firstModifiedLine &&
      lastModifiedLine
    ) {
      showModifiedLineOption = true
      modifiedRangeText = generateModifiedRangeText(
        firstModifiedLine,
        lastModifiedLine,
        originalLinesAreEmpty,
        isSplit,
        allLinesAreContextLines,
      )
    }

    return (
      <>
        {isSplit ? (
          <>
            {allLinesAreContextLines ? (
              <>
                <ActionList.Item onSelect={handleStartModifiedLinesConversationSelection}>
                  <ActionList.LeadingVisual>
                    <PlusIcon />
                  </ActionList.LeadingVisual>
                  {START_CONVERSATION + modifiedRangeText}
                  <ActionList.TrailingVisual>
                    <KeybindingHint keys="Alt+n" />
                  </ActionList.TrailingVisual>
                </ActionList.Item>
                <ActionList.Item
                  onSelect={e => handleStartModifiedLinesConversationSelection(e, {withSuggestedChange: true})}
                >
                  <ActionList.LeadingVisual>
                    <PlusCircleIcon />
                  </ActionList.LeadingVisual>
                  {SUGGEST_CHANGE + modifiedRangeText}
                </ActionList.Item>
              </>
            ) : (
              <>
                {showOriginalLineOption ? (
                  <>
                    <ActionList.Item onSelect={handleStartOriginalLinesConversationSelection}>
                      <ActionList.LeadingVisual>
                        <PlusIcon />
                      </ActionList.LeadingVisual>
                      {START_CONVERSATION + originalRangeText}
                      <ActionList.TrailingVisual>
                        <KeybindingHint keys={modifiedLinesAreEmpty ? 'Alt+n' : 'Alt+Shift+N'} />
                      </ActionList.TrailingVisual>
                    </ActionList.Item>
                    {allOriginalLinesNotDeletions && (
                      <ActionList.Item
                        onSelect={e => handleStartOriginalLinesConversationSelection(e, {withSuggestedChange: true})}
                      >
                        <ActionList.LeadingVisual>
                          <PlusCircleIcon />
                        </ActionList.LeadingVisual>
                        {SUGGEST_CHANGE + originalRangeText}
                      </ActionList.Item>
                    )}
                  </>
                ) : null}
                {showModifiedLineOption ? (
                  <>
                    <ActionList.Item onSelect={handleStartModifiedLinesConversationSelection}>
                      <ActionList.LeadingVisual>
                        <PlusIcon />
                      </ActionList.LeadingVisual>
                      {START_CONVERSATION + modifiedRangeText}
                      <ActionList.TrailingVisual>
                        <KeybindingHint keys="Alt+n" />
                      </ActionList.TrailingVisual>
                    </ActionList.Item>
                    <ActionList.Item
                      onSelect={e => handleStartModifiedLinesConversationSelection(e, {withSuggestedChange: true})}
                    >
                      <ActionList.LeadingVisual>
                        <PlusCircleIcon />
                      </ActionList.LeadingVisual>
                      {SUGGEST_CHANGE + modifiedRangeText}
                    </ActionList.Item>
                  </>
                ) : null}
              </>
            )}
          </>
        ) : (
          <>
            <ActionList.Item onSelect={handleStartConversation}>
              <ActionList.LeadingVisual>
                <PlusIcon />
              </ActionList.LeadingVisual>
              {START_CONVERSATION + unifiedRangeText}
            </ActionList.Item>
            {canSuggestChanges && allOriginalLinesNotDeletions && (
              <ActionList.Item onSelect={handleStartConversationWithSuggestedChange}>
                <ActionList.LeadingVisual>
                  <PlusCircleIcon />
                </ActionList.LeadingVisual>
                {SUGGEST_CHANGE + unifiedRangeText}
              </ActionList.Item>
            )}
          </>
        )}
      </>
    )
  }

  // Single Line selection
  return (
    <>
      <ActionList.Item onSelect={handleStartConversation}>
        <ActionList.LeadingVisual>
          <PlusIcon />
        </ActionList.LeadingVisual>
        {generateSingleLineText(START_CONVERSATION, line, isLeftSide)}
      </ActionList.Item>
      {canSuggestChanges && (
        <ActionList.Item onSelect={handleStartConversationWithSuggestedChange}>
          <ActionList.LeadingVisual>
            <PlusCircleIcon />
          </ActionList.LeadingVisual>
          {generateSingleLineText(SUGGEST_CHANGE, line, isLeftSide)}
        </ActionList.Item>
      )}
    </>
  )
}

try{ StartConversationContextMenuItems.displayName ||= 'StartConversationContextMenuItems' } catch {}